/**
 * prices.js - Price fetching and management engine
 *
 * Handles fetching prices from multiple sources:
 * - Twelve Data (TD) for US stocks, ETFs, commodities
 * - CoinGecko (CG) for cryptocurrencies
 * - Google Apps Script proxy for BIST Turkish stocks
 * - Exchange Rate API for FX rates
 *
 * Exports:
 * - PS: Price source map constant
 * - Functions: safeFetch, updatePriceStatus, tdFetchChunk, cgFetch, fetchFx,
 *   bistProxyFetch, resolveBistProxy, tefasFetch, refreshPrices, setupPoll, sleep
 * - State setters: _setPriceState, _setOnPricesRefreshed
 */

import { save } from './state.js';
import { getMs as _getMs } from './math.js';
import { toast } from '../ui/layout.js';
import { f } from './utils.js';
import { scanTradeCrossings } from '../modules/trades.js';
import { fetchPricesFromAPI } from './price-api.js';
import { fetchWithRetry, breakers } from './resilience.js';

// ===== STATE INJECTION =====
let _S = null;
export function _setPriceState(s) {
  _S = s;
}

let _onPricesRefreshed = null;
export function _setOnPricesRefreshed(fn) {
  _onPricesRefreshed = fn;
}

// ===== CONSTANTS & CONFIG =====
// Rate limit: Twelve Data free tier = 8 credits/minute, 1 credit per symbol
const TD_CREDITS_PER_MIN = 8;
const TD_WAIT_MS = 62000; // wait 62s between batches to be safe

/**
 * Price Source Map (PS) — defines how to fetch prices for each ticker
 *
 * Properties:
 * - s: source type ('bist'|'td'|'fx'|'cg'|'m')
 * - td: Twelve Data symbol (e.g. 'NVDA', 'XAU/USD')
 * - cg: CoinGecko ID (e.g. 'bitcoin', 'ethereum')
 * - tx: transformation ('gram' for ounces→grams conversion)
 * - yf: Yahoo Finance fallback symbol
 */
export const PS = {
  /// BIST Stocks — via Google Apps Script proxy (Yahoo Finance server-side)
  ENJSA: { s: 'bist' },
  ISGYO: { s: 'bist' },
  ASELS: { s: 'bist' },
  THYAO: { s: 'bist' },
  TUPRS: { s: 'bist' },
  // Commodities — Twelve Data: XAU/USD, XAG/USD (oz prices, convert to gram TRY)
  XAU: { s: 'td', td: 'XAU/USD', yf: 'GC=F', tx: 'gram' },
  XAG: { s: 'td', td: 'XAG/USD', yf: 'SI=F', tx: 'gram' },
  // FX
  EUR: { s: 'fx' },
  USD: { s: 'fx' },
  // Turkish funds — TEFAS's own JSON API via /api/tefas, server-side.
  // These carried `{ s: 'm' }` and the comment "manual only (TEFAS, no API)" until
  // 2026-08-03, when they were the entirety of the watchdog's "7 prices older than 24h":
  // every fund in the book valued at whatever was last typed in. The endpoint exists — the
  // site was rebuilt in 2026 and its own front end calls it. What was missing was the
  // assumption, not the API. Manual entry still wins if the fetch fails.
  HVZ: { s: 'tefas' },
  IDH: { s: 'tefas' },
  OTJ: { s: 'tefas' },
  TEJ: { s: 'tefas' },
  AFA: { s: 'tefas' },
  YKT: { s: 'tefas' },
  NVB: { s: 'tefas' },
  // US Stocks
  NVDA: { s: 'td', td: 'NVDA' },
  MSFT: { s: 'td', td: 'MSFT' },
  CVX: { s: 'td', td: 'CVX' },
  ENB: { s: 'td', td: 'ENB' },
  AVGO: { s: 'td', td: 'AVGO' },
  // US ETFs
  VOO: { s: 'td', td: 'VOO' },
  QQQ: { s: 'td', td: 'QQQ' },
  SMH: { s: 'td', td: 'SMH' },
  XLE: { s: 'td', td: 'XLE' },
  XLV: { s: 'td', td: 'XLV' },
  DBA: { s: 'td', td: 'DBA' },
  COPX: { s: 'td', td: 'COPX' },
  CLOU: { s: 'td', td: 'CLOU' },
  // EU Stocks (XETRA) — via Yahoo Finance proxy as SAP.DE
  SAP: { s: 'yf', yf: 'SAP.DE' },
  // Crypto — CoinGecko via server API
  BTC: { s: 'cg', cg: 'bitcoin' },
  ETH: { s: 'cg', cg: 'ethereum' },
  XRP: { s: 'cg', cg: 'ripple' },
  // Benchmarks
  XU100: { s: 'bist' }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Safe fetch with timeout and content-type validation
 */
export async function safeFetch(url, opts = {}) {
  const timeout = opts.timeout || 10000;
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeout);
  try {
    const r = await fetch(url, { ...opts, signal: ac.signal });
    clearTimeout(timer);
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const ct = r.headers.get('content-type') || '';
    if (!ct.includes('json') && !ct.includes('text'))
      console.warn('[ZAGX] Unexpected content-type:', ct, 'for', url);
    return r;
  } catch (e) {
    clearTimeout(timer);
    if (e.name === 'AbortError')
      throw new Error('Request timeout (' + Math.round(timeout / 1000) + 's): ' + url);
    throw e;
  }
}

/**
 * Update price status indicator in DOM
 */
/**
 * GAS/BIST proxy health (audit backlog: GAS fallback).
 * The proxy is the ONLY source for TRY equities — when it fails, prices keep
 * their last-known values silently. This makes the degradation visible once
 * per outage (deduped toast + stale status) and announces recovery. Manual
 * price entry remains the documented fallback path.
 * @returns {boolean} true when any requested BIST ticker got no fresh price
 */
let _bistDown = false;
export function _noteBistHealth(requested, results) {
  const missing = (requested || []).filter(tk => !results || !results[tk]);
  if (missing.length) {
    if (!_bistDown) {
      _bistDown = true;
      toast(`BIST proxy unreachable — ${missing.length} TRY price(s) showing last known values. Manual price entry stays available.`, 'a-y', 8000);
    }
    return true;
  }
  if (_bistDown && (requested || []).length) {
    _bistDown = false;
    toast('BIST proxy recovered — TRY prices live again', 'a-g');
  }
  return false;
}

export function updatePriceStatus(msg, dot = 'dot-live') {
  const el = document.getElementById('priceStatus');
  if (el) el.innerHTML = `<span class="dot ${dot}"></span>&nbsp;${msg}`;
}

/**
 * Sleep utility (promise-based delay)
 */
export function sleep(ms) {
  return new Promise(r => setTimeout(r, ms));
}

// ===== FETCH FUNCTIONS BY SOURCE =====

/**
 * ⚠ SEC-005 — THE TWELVE DATA KEY GOES IN A HEADER, AND FALLS BACK TO THE QUERY STRING.
 *
 * `Authorization: apikey <key>` is what Twelve Data documents and recommends. The reason this
 * item sat deferred for days is real and has not gone away: **an `Authorization` header makes the
 * request non-simple, which triggers a CORS preflight, and if their preflight does not allow the
 * header the price feed dies outright.** That question could not be answered from here — this
 * container has no egress to `api.twelvedata.com`, and an in-browser probe failed identically for
 * the header AND the query-string form, so it measured the probe, not the API. **An unverified
 * CORS assumption is not something to ship into a live price feed.**
 *
 * So the transport is not switched. It is ATTEMPTED, once, and it steps aside if it cannot work:
 *
 *   - the header attempt runs with `retries: 0` and **no circuit breaker** — a preflight refusal
 *     must not burn API credits or trip the circuit that guards the real path;
 *   - a failure carrying an HTTP `status` means the server ANSWERED, so the transport is fine and
 *     the error is a real one (a bad key, a rate limit) — it is rethrown, not papered over;
 *   - only a transport failure with no status (the shape a CORS refusal takes) downgrades, once,
 *     for the rest of the session, with a line in the console saying so;
 *   - a transient timeout on the very first call downgrades too. That is a downgrade to exactly
 *     today's behaviour, and the next session tries the header again.
 *
 * ⚠ AND IT IS SAFE TO SHIP TODAY FOR A REASON THAT IS NOT ABOUT THE CODE: measured on the live
 * book 2026-08-17, `settings.tdApiKey` is **not set**. Prices come from the server API, this whole
 * client-side branch is a fallback guarded by `if (apiKey)`, and it does not execute. The item was
 * dormant. This is the staged plan landing while the stage is empty.
 */
let _tdAuth = 'unknown';                     // 'unknown' | 'header' | 'query'
export const _tdAuthMode = () => _tdAuth;
export const _resetTdAuth = () => { _tdAuth = 'unknown'; };

export async function tdRequest(baseUrl, apiKey, opts = {}) {
  const sep = baseUrl.includes('?') ? '&' : '?';
  const viaQuery = () => fetchWithRetry(`${baseUrl}${sep}apikey=${encodeURIComponent(apiKey)}`, opts);
  if (_tdAuth === 'query') return viaQuery();
  try {
    const r = await fetchWithRetry(baseUrl, {
      ...opts,
      retries: 0,
      breaker: null,
      headers: { ...(opts.headers || {}), Authorization: `apikey ${apiKey}` },
    });
    _tdAuth = 'header';
    return r;
  } catch (e) {
    // ⚠ THIS LINE WAS MISSING AND ITS OWN TEST FOUND IT. The docblock above promised that a blip
    // AFTER the header is known to work is rethrown rather than downgraded — and the code did not
    // say so. Without it one flaky moment migrates him to the query string for the rest of the
    // session and nothing ever migrates him back. A comment is not an implementation.
    if (_tdAuth === 'header') throw e;
    // The server answered — the header reached it. Whatever went wrong is not the transport.
    if (e && e.status) { _tdAuth = 'header'; throw e; }
    console.warn('[TD] header auth unavailable, using the query string for this session:', (e && e.message) || e);
    _tdAuth = 'query';
    return viaQuery();
  }
}

/**
 * Single TD batch request — returns map of tk→priceObj
 */
export async function tdFetchChunk(chunk, apiKey) {
  const results = {};
  const symStr = chunk.map(s => s.td).join(',');

  // Circuit breaker gate — fail fast if TD API is down
  if (!breakers.td.canRequest()) {
    console.warn('[TD] Circuit OPEN — skipping batch:', symStr);
    return results;
  }

  try {
    const r = await tdRequest(
      `https://api.twelvedata.com/quote?symbol=${symStr}`,
      apiKey,
      {
        timeout: 15000,
        retries: 1,           // 1 retry (2 total) — preserve API credits
        backoffBase: 2000,
        breaker: breakers.td,
        onRetry: (attempt, err) => console.log('[TD] Retry', attempt, ':', err.message)
      }
    );
    const d = await r.json();
    if (d.code) {
      console.warn('[TD] API error:', d.code, d.message);
      // API-level error (bad key, rate limit) — record as failure
      if (d.code === 429 || d.code === 401 || d.code === 403) breakers.td.recordFailure();
      return results;
    }
    if (chunk.length === 1) {
      const s = chunk[0];
      if (d.close)
        results[s.tk] = {
          price: parseFloat(d.close),
          changePct: parseFloat(d.percent_change) || 0,
          src: 'auto',
          ts: Date.now(),
          cur: d.currency || 'USD',
          name: d.name
        };
    } else {
      chunk.forEach(s => {
        const q =
          d[s.td] ||
          d[s.tk] ||
          d[s.td.split(':')[0]] ||
          d[s.td.split('/')[0]];
        if (q && q.close && !q.code) {
          results[s.tk] = {
            price: parseFloat(q.close),
            changePct: parseFloat(q.percent_change) || 0,
            src: 'auto',
            ts: Date.now(),
            cur: q.currency || 'USD',
            name: q.name
          };
        }
      });
    }
  } catch (e) {
    console.warn('[TD] Error:', e.message || e);
  }
  return results;
}

/**
 * CoinGecko — free, CORS-enabled, no rate issues
 * ids can be comma-separated: 'bitcoin,ethereum'
 */
export async function cgFetch(ids) {
  // Circuit breaker gate
  if (!breakers.cg.canRequest()) {
    console.warn('[CG] Circuit OPEN — skipping crypto fetch');
    return null;
  }

  try {
    const r = await fetchWithRetry(
      `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`,
      {
        timeout: 10000,
        retries: 2,           // 2 retries (3 total) — free API, retry more
        backoffBase: 1000,
        breaker: breakers.cg,
        onRetry: (attempt, err) => console.log('[CG] Retry', attempt, ':', err.message)
      }
    );
    return await r.json();
  } catch (e) {
    console.warn('[CG] Error:', e.message);
    return null;
  }
}

/**
 * FX rates via exchangerate-api (free, CORS-enabled, saves TD credits)
 */
export async function fetchFx() {
  // Circuit breaker gate
  if (!breakers.fx.canRequest()) {
    console.warn('[FX] Circuit OPEN — using cached rate');
    _useCachedFx();
    _updateFxDisplay();
    return;
  }

  try {
    const r = await fetchWithRetry('https://api.exchangerate-api.com/v4/latest/USD', {
      timeout: 8000,
      retries: 2,
      backoffBase: 1000,
      breaker: breakers.fx,
      onRetry: (attempt, err) => console.log('[FX] Retry', attempt, ':', err.message)
    });
    const d = await r.json();
    // R-1 Fix: validate before assigning — never overwrite a good rate with a malformed/zero response (prevents silent $0 valuations)
    const tryRate = Number(d?.rates?.TRY);
    const eurRate = Number(d?.rates?.EUR);
    if (tryRate > 0 && eurRate > 0) {
      _S.fxRate = tryRate;
      _S.fxRateEUR = tryRate / eurRate;
      // CE-06 Fix: Store direct EUR/USD rate to avoid compounded rounding via TRY cross
      _S.fxEURUSD = eurRate; // EUR per 1 USD (e.g. 0.92)
      // LSE UCITS lines can quote in GBP/GBp — same response already carries the rate
      const gbpRate = Number(d?.rates?.GBP);
      if (gbpRate > 0) _S.fxGBPUSD = 1 / gbpRate; // USD per 1 GBP
      _S._fxTs = Date.now(); // STABILIZATION: track FX freshness
    } else {
      console.warn('[FX] Response missing/invalid rates — keeping last-known rate', d?.rates);
      _useCachedFx();
    }
  } catch (e) {
    console.warn('[FX] All retries failed:', e.message);
    _useCachedFx();
  }
  _updateFxDisplay();
}

/** Fallback: load cached FX from monthlyRates */
function _useCachedFx() {
  const ms = _getMs();
  if (ms.length && !_S.fxRate) {
    const key = ms[ms.length - 1];
    const l = _S.monthlyRates[key];
    if (l) {
      _S.fxRate = l.usdtry;
      _S.fxRateEUR = l.eurtry;
      // A monthly-average rate is stale by construction. Stamp it with the month it
      // came from so the header shows its real age instead of reading as live.
      _S._fxTs = Date.parse(key + '-01T00:00:00Z') || 0;
    }
  }
}

/** Update FX display elements + staleness indicator */
function _updateFxDisplay() {
  const usdEl = document.getElementById('fxUSD');
  const eurEl = document.getElementById('fxEUR');
  if (!usdEl || !eurEl) return; // NS-01: guard for early price refresh before DOM ready
  usdEl.textContent = _S.fxRate ? f(_S.fxRate, 2) : '—';
  eurEl.textContent = _S.fxRateEUR ? f(_S.fxRateEUR, 2) : '—';
  const xauP = _S.prices['XAU']?.price;
  document.getElementById('fxXAU').textContent = xauP ? f(xauP, 2) : '—';
  document.getElementById('fxINFL').textContent =
    ((_S.settings.trInflation || 0) * 100).toFixed(1) + '%';
  // FRESHNESS INTEGRITY: a missing _fxTs means the rate's age is UNKNOWN (cloud-restored
  // session, or a code path that never stamped it). Unknown age must read as stale, never
  // as fresh — the old `_S._fxTs && ...` short-circuited to "fresh" in exactly that case.
  const fxAge = _S._fxTs ? Date.now() - _S._fxTs : null;
  const stale = _S.fxRate ? (fxAge === null || fxAge > 3600000) : false;
  if (stale) {
    let label = 'of unknown age';
    if (fxAge !== null) {
      const mins = Math.round(fxAge / 60000);
      label = mins < 60 ? mins + 'm ago' : mins < 1440 ? Math.round(mins / 60) + 'h ago' : Math.round(mins / 1440) + 'd ago';
    }
    usdEl.title = 'FX rate is ' + label;
    eurEl.title = 'FX rate is ' + label;
    usdEl.style.opacity = '0.6';
    eurEl.style.opacity = '0.6';
  } else {
    usdEl.title = '';
    eurEl.title = '';
    usdEl.style.opacity = '';
    eurEl.style.opacity = '';
  }
}

/**
 * Fetch prices via Google Apps Script Yahoo Finance proxy
 * @param {string[]} tickers - Ticker symbols
 * @param {string} proxyUrl - GAS proxy URL
 * @param {Object} [suffixMap] - Optional map of tk → Yahoo Finance symbol (e.g. { SAP: 'SAP.DE' }). Default: tk + '.IS'
 */
/**
 * Normalize pence/GBP quotes to USD at ingestion. Consumers value holdings by the
 * HOLDING's currency, so a GBp/GBP-quoted feed on a USD holding would be off 100x/1.3x.
 * Returns null when a GBP quote cannot be converted (no fxGBPUSD yet) — no price is
 * safer than a wrong-currency price; the holding falls back to manual-price mode.
 */
export function normalizeQuote(p) {
  if (!p || !p.price) return p;
  if (p.cur === 'GBp' || p.cur === 'GBX') { p.price = p.price / 100; p.cur = 'GBP'; }
  if (p.cur === 'GBP') {
    if (_S && _S.fxGBPUSD > 0) { p.price = p.price * _S.fxGBPUSD; p.cur = 'USD'; }
    else { console.warn('[Price] GBP quote dropped — no GBPUSD rate yet:', p.name || ''); return null; }
  }
  return p;
}

/**
 * Which URL actually serves BIST quotes.
 *
 * `settings.bistProxyUrl` held a Google Apps Script /exec address that the BROWSER fetched
 * directly. On 2026-08-03 every one of those calls was refused: an /exec answers with a 302
 * to script.googleusercontent.com and that hop sends no Access-Control-Allow-Origin header,
 * so the response is unreadable regardless of what the script computed. Fifteen tickers —
 * the whole TR equity sleeve — reported "no data" and the circuit breaker opened.
 *
 * A cross-origin rule is enforced by the browser, so no edit to the script can fix it. A
 * stored setting naming a URL the browser is structurally unable to read is not a
 * configuration; it is a dead pointer, and honouring it would keep the sleeve dark. The
 * built-in /api/bist function fetches the same Yahoo data server-side, where CORS does not
 * apply.
 *
 * A non-Google override still wins, because someone running their own proxy has a reason to.
 */
export function resolveBistProxy(url) {
  const u = String(url || '').trim();
  if (!u) return '/api/bist';
  // Matched on the HOST, not on the string. A self-hosted relay whose path happens to
  // mention script.google.com is a working proxy and must not be swapped out from under it.
  // The base makes a relative setting ('/api/bist') parse instead of throwing.
  let host = '';
  try { host = new URL(u, 'https://relative.invalid').hostname.toLowerCase(); } catch (_e) { host = ''; }
  if (/(^|\.)script\.google\.com$/.test(host) || /(^|\.)script\.googleusercontent\.com$/.test(host)) return '/api/bist';
  return u;
}

/**
 * TEFAS fund NAVs, server-side.
 *
 * A NAV is a DAILY CLOSE, not a quote: funds price once after the close and not at all on
 * weekends or holidays. The `ts` that comes back is the date the NAV was STRUCK, and it is
 * passed through untouched — stamping it with the fetch time would make a four-day-old close
 * read as fresh, which is exactly the silent degradation this codebase keeps finding.
 *
 * On failure the caller keeps whatever price is already stored, which for these seven
 * positions is the owner's own manual entry. An automatic source that quietly returns nothing
 * is worse than the manual entry it replaced: he would stop typing prices in and never learn
 * that nothing took over.
 */
export async function tefasFetch(codes, fetchImpl) {
  const results = {};
  if (!codes || !codes.length) return results;
  const doFetch = fetchImpl || (url => fetch(url).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  }));
  let data;
  try {
    data = await doFetch('/api/tefas?codes=' + encodeURIComponent(codes.join(',')));
  } catch (e) {
    console.warn('[TEFAS] fetch failed:', (e && e.message) || e);
    return results;
  }
  if (!data || typeof data !== 'object' || data.error) {
    console.warn('[TEFAS] no usable payload:', data && data.error);
    return results;
  }
  codes.forEach(tk => {
    const d = data[tk];
    if (!d || !(d.price > 0)) { console.warn('[TEFAS] ✗', tk, ': no NAV'); return; }
    results[tk] = {
      price: d.price,
      changePct: d.changePct || 0,
      src: 'auto',
      ts: d.ts || Date.now(),          // the close date, not now()
      cur: d.currency || 'TRY',
      name: d.name || tk,
    };
    // warn, not log: the production bundle gates console.log, which is exactly why the
    // first TEFAS deploy looked like it did nothing at all.
    console.warn('[TEFAS] ✓', tk, ':', d.price, 'as of', new Date(results[tk].ts).toISOString().slice(0, 10));
  });
  return results;
}

export async function bistProxyFetch(tickers, proxyUrl, suffixMap) {
  const results = {};
  proxyUrl = resolveBistProxy(proxyUrl);
  if (!tickers.length) return results;

  // Circuit breaker gate
  if (!breakers.bist.canRequest()) {
    console.warn('[BIST] Circuit OPEN — skipping proxy fetch');
    return results;
  }

  // Chunked because the Apps Script relay had a 30-second execution ceiling. /api/bist does
  // not: it fetches the symbols in parallel and answers in well under a second, so the
  // chunk exists now only to bound one request, and fewer, larger calls mean fewer function
  // invocations. Kept at or below the function's own MAX_SYMBOLS of 20.
  const CHUNK_SIZE = 12;
  const chunks = [];
  for (let i = 0; i < tickers.length; i += CHUNK_SIZE) {
    chunks.push(tickers.slice(i, i + CHUNK_SIZE));
  }
  console.log('[BIST] Fetching', tickers.length, 'tickers in', chunks.length, 'chunks');

  // Fetch all chunks in parallel
  const chunkResults = await Promise.allSettled(chunks.map(async (chunk, idx) => {
    const yfSymbols = chunk.map(tk => (suffixMap && suffixMap[tk]) || (tk + '.IS')).join(',');
    try {
      const r = await fetchWithRetry(proxyUrl + '?symbols=' + encodeURIComponent(yfSymbols), {
        timeout: 30000,
        retries: 1,
        backoffBase: 3000,
        breaker: breakers.bist,
        onRetry: (attempt, err) => console.log('[BIST] Chunk', idx + 1, 'retry', attempt, ':', err.message)
      });
      return await r.json();
    } catch (e) {
      console.warn('[BIST] Chunk', idx + 1, 'error:', e.message);
      return {};
    }
  }));

  // Merge chunk results
  for (const cr of chunkResults) {
    const d = cr.status === 'fulfilled' ? cr.value : {};
    for (const tk of tickers) {
      if (d[tk] && d[tk].price && !d[tk].error) {
        const q = normalizeQuote({
          price: d[tk].price,
          changePct: d[tk].changePct || 0,
          src: 'auto',
          ts: d[tk].ts || Date.now(),
          cur: d[tk].currency || 'TRY',
          name: d[tk].name || tk
        });
        if (q) results[tk] = q;
        console.log('[BIST] ✓', tk, ':', d[tk].price, d[tk].currency);
      }
    }
  }

  // Log failures
  tickers.forEach(tk => { if (!results[tk]) console.warn('[BIST] ✗', tk, ': no data'); });

  return results;
}

// ===== SERVER API INTEGRATION =====

/**
 * Try fetching prices via server API first.
 * If successful, returns true and updates state.
 * If API unavailable, returns false → caller falls back to client-side.
 */
async function _tryApiPrices(tickers) {
  try {
    // EU/Yahoo-mapped holdings must be requested by their Yahoo symbol (SAP → SAP.DE);
    // the server cannot price the bare ticker (it resolves to the US listing or fails).
    // Translate out, then map the response key back to the holding ticker.
    const _rev = {};
    const _apiTickers = tickers.map(tk => {
      const m = PS[tk];
      const sym = (m && m.s === 'yf' && m.yf) ? m.yf : tk;
      _rev[sym] = tk;
      return sym;
    });
    const apiResult = await fetchPricesFromAPI(_apiTickers);
    if (!apiResult) return false;
    // A 200 with zero usable prices is a failure, not a success. fetchAllPrices never
    // throws — it returns { prices: {}, stats: { fail: N } } on a total upstream outage,
    // and this function used to return true for it, so the client fallback never ran and
    // the pill went green. (audit 2026-08-01)
    const _stats = apiResult.stats || { ok: 0, fail: 0 };
    if (_apiTickers.length && !(_stats.ok > 0)) {
      console.warn('[Price] Server API returned 0 usable prices —', _stats.fail, 'failed; falling back to client fetch');
      return false;
    }

    // Apply FX rates.
    // Same validation as fetchFx() below: never overwrite a good rate with a
    // malformed/partial response. This path is the PRIMARY one and had no guard,
    // so a { fx: {} } reply assigned undefined and every TRY holding silently
    // fell back to a 1 TRY = 1 USD valuation.
    if (apiResult.fx) {
      const _try = Number(apiResult.fx.usdtry);
      const _eur = Number(apiResult.fx.eurtry);
      if (_try > 0 && _eur > 0) {
        _S.fxRate = _try;
        _S.fxRateEUR = _eur;
        const _eu = Number(apiResult.fx.eurusd);
        if (_eu > 0) _S.fxEURUSD = _eu;
        _S._fxTs = Date.now();
      } else {
        console.warn('[FX] Server API rates missing/invalid — keeping last-known rate', apiResult.fx);
      }
    }

    // Apply prices — map Yahoo symbols back to the holding ticker (SAP.DE → SAP)
    if (apiResult.prices) {
      for (const [tk, data] of Object.entries(apiResult.prices)) {
        const q = normalizeQuote(data);
        if (q) _S.prices[_rev[tk] || tk] = q;
      }
    }

    // Update FX display via the shared helper so the staleness indicator applies
    // here too — the old inline copy skipped it entirely on the primary path.
    _updateFxDisplay();

    console.log('[Price] Server API:', _stats.ok || 0, 'ok,', _stats.fail || 0, 'fail');
    // Return the stats so the caller can report what actually came back rather than
    // counting the cache — `${Object.keys(_S.prices).length} prices` was reporting
    // yesterday's cached tickers as today's live fetch.
    return { ok: _stats.ok || 0, fail: _stats.fail || 0 };
  } catch (e) {
    console.warn('[Price] API fallback:', e.message);
    return false;
  }
}

// ===== MAIN PRICE REFRESH ENGINE =====

/**
 * The set of tickers a price feed should be asked for.
 *
 * TimeDeposits are excluded: they are valued from t.td.principal + tdAccrued(), never from
 * a feed. Asking for one was a guaranteed permanent miss — a TRY ticker like TRYVD2
 * auto-resolves to a BIST equity, Yahoo has no TRYVD2.IS, and _noteBistHealth then latched
 * _bistDown = true on the first refresh and never reset. The outage toast fired once ever,
 * the recovery toast never fired, and a REAL BIST proxy outage was therefore invisible.
 * (audit 2026-08-01)
 *
 * Exported so the exclusion is asserted by a test rather than assumed.
 */
export function priceableTickers(s) {
  const tks = new Set();
  (s.holdings || []).forEach(h => { if (h && h.ticker) tks.add(h.ticker); });
  (s.trades || []).forEach(t => {
    if (!t || !t.ticker) return;
    if (t.status === 'Closed') return;
    if (t.type === 'TimeDeposit') return;
    tks.add(t.ticker);
  });
  tks.add('XU100');
  return [...tks];
}

/**
 * Apply TEFAS NAVs. Called from BOTH refresh paths.
 *
 * The first wiring put this only in the client-side fallback — and `/api/prices` succeeds on
 * every normal load, so the branch returned before ever reaching it. Seven funds kept their
 * manual prices, the function was provably working, and nothing in the console said why.
 * A source wired only into the failure path is not wired. (audit 2026-08-03)
 */
async function _applyTefas(tks) {
  const codes = [...tks].filter(tk => PS[tk] && PS[tk].s === 'tefas');
  if (!codes.length) return { ok: 0, missing: [] };
  const res = await tefasFetch(codes);
  Object.keys(res).forEach(tk => { _S.prices[tk] = res[tk]; });
  const missing = codes.filter(tk => !res[tk]);
  // Silence is not success. If every fund came back empty the manual prices stand, and the
  // owner has to be told that they are standing.
  if (missing.length && missing.length === codes.length) {
    toast(`TEFAS unreachable — ${missing.length} fund NAV(s) showing last manual entry.`, 'a-y', 8000);
  } else if (missing.length) {
    console.warn('[TEFAS] no NAV for', missing.join(', '), '— keeping their manual prices');
  }
  return { ok: Object.keys(res).length, missing };
}

let _priceRefreshing = false;

/**
 * Main price refresh function
 * Priority: Server API → Client-side direct fetch (fallback)
 * Modifies _S.prices and _S.fxRate directly
 * Calls scanTradeCrossings after prices are updated
 */
export async function refreshPrices() {
  if (_priceRefreshing) {
    console.log('[Price] Already refreshing, skip');
    return;
  }
  _priceRefreshing = true;
  document.body.classList.add('shimmer-loading');
  try {
  const _prevPrices = JSON.parse(JSON.stringify(_S.prices || {}));

  // ─── Try server API first ───
  const tks = new Set(priceableTickers(_S));

  const apiSuccess = await _tryApiPrices([...tks]);
  if (apiSuccess) {
    // Fund NAVs first: this branch has two early returns below, and anything placed after
    // them runs only on the paths that do not take them.
    await _applyTefas(tks);
    // Server API succeeded but may not cover BIST stocks — run BIST proxy for missing prices
    const bistProxyUrl = resolveBistProxy(_S.settings.bistProxyUrl);
    if (bistProxyUrl) {
      const bistTickers = [];
      for (const tk of tks) {
        let ps = PS[tk];
        if (!ps) {
          const hold = _S.holdings.find(h => h.ticker === tk);
          const trade = _S.trades.find(t => t.ticker === tk && t.status !== 'Closed');
          const cur = trade?.currency || hold?.currency || 'USD';
          // const tp = trade?.type || hold?.type || 'Stock'; // unused
          if (cur === 'TRY') { PS[tk] = { s: 'bist' }; ps = PS[tk]; }
        }
        if (ps && ps.s === 'bist') {
          bistTickers.push(tk);
        }
      }
      if (bistTickers.length) {
        console.log('[BIST] Post-API: fetching', bistTickers.length, 'missing BIST tickers');
        const bistRes = await bistProxyFetch(bistTickers, bistProxyUrl);
        for (const tk of Object.keys(bistRes)) {
          _S.prices[tk] = bistRes[tk];
        }
        // Visible degradation: proxy failures used to vanish behind a green "Server API" pill
        if (_noteBistHealth(bistTickers, bistRes)) {
          const nMiss = bistTickers.filter(tk => !bistRes[tk]).length;
          updatePriceStatus(`Server API · BIST proxy degraded · ${nMiss} stale`, 'dot-stale');
          try { scanTradeCrossings(_prevPrices); } catch (e) { console.warn('Alert scan err', e); }
          save();
          if (_onPricesRefreshed) _onPricesRefreshed();
          return;
        }
      }
    }
    const _pf = (apiSuccess && apiSuccess.fail) || 0;
    const _pok = (apiSuccess && apiSuccess.ok) || 0;
    updatePriceStatus(
      _pf ? `Server API · ${_pok} live · ${_pf} failed` : `Server API · ${_pok} live`,
      _pf ? 'dot-stale' : 'dot-live');
    try { scanTradeCrossings(_prevPrices); } catch (e) { console.warn('Alert scan err', e); }
    save();
    if (_onPricesRefreshed) _onPricesRefreshed();
    return;
  }

  // ─── Fallback: client-side direct fetch ───
  console.log('[Price] Using client-side direct fetch');
  updatePriceStatus('Fetching...', 'dot-live');
  let ok = 0,
    fail = 0;
  const apiKey = _S.settings.tdApiKey || '';
  // tks already populated above (before API attempt)

  // 1. FX rates — always use free exchangerate-api (saves TD credits)
  await fetchFx();

  // 2. CoinGecko for crypto (free, no rate limit issues)
  const cgMap = {};
  const cgIds = [];
  for (const tk of tks) {
    const ps = PS[tk];
    if (ps && ps.cg) {
      cgMap[ps.cg] = tk;
      cgIds.push(ps.cg);
    }
  }
  if (cgIds.length) {
    const cgData = await cgFetch(cgIds.join(','));
    if (cgData) {
      cgIds.forEach(id => {
        const tk = cgMap[id];
        if (cgData[id]) {
          _S.prices[tk] = {
            price: cgData[id].usd,
            changePct: cgData[id].usd_24h_change || 0,
            src: 'auto',
            ts: Date.now(),
            cur: 'USD'
          };
          ok++;
        }
      });
    }
  }

  // 3. FX-derived prices (EUR, USD as TRY holdings)
  for (const tk of tks) {
    const ps = PS[tk];
    if (!ps || ps.s !== 'fx') continue;
    if (tk === 'USD' && _S.fxRate) {
      _S.prices[tk] = {
        price: _S.fxRate,
        changePct: 0,
        src: 'auto',
        ts: Date.now(),
        cur: 'TRY'
      };
      ok++;
    } else if (tk === 'EUR' && _S.fxRateEUR) {
      _S.prices[tk] = {
        price: _S.fxRateEUR,
        changePct: 0,
        src: 'auto',
        ts: Date.now(),
        cur: 'TRY'
      };
      ok++;
    }
  }

  // 4a. BIST stocks via the server-side quote proxy (/api/bist, or a self-hosted override)
  const bistProxyUrl = resolveBistProxy(_S.settings.bistProxyUrl);
  const bistTickers = [];
  for (const tk of tks) {
    let ps = PS[tk];
    // Auto-resolve unknown tickers
    if (!ps) {
      const trade = _S.trades.find(t => t.ticker === tk && t.status !== 'Closed');
      const hold = _S.holdings.find(h => h.ticker === tk);
      const cur = trade?.currency || hold?.currency || 'USD';
      const tp = trade?.type || hold?.type || 'Stock';
      if (tp === 'Crypto') {
        const cgId = tk.toLowerCase();
        PS[tk] = { s: 'cg', td: tk + '/USD', cg: cgId };
        ps = PS[tk];
      } else if (cur === 'TRY') {
        PS[tk] = { s: 'bist' };
        ps = PS[tk];
      } else {
        PS[tk] = { s: 'td', td: tk };
        ps = PS[tk];
      }
      console.log('[Auto] Mapped', tk, '→ source:', ps.s);
    }
    // Collect BIST stocks: explicitly marked as bist, OR td with :BIST suffix
    if (ps.s === 'bist' || (ps.s === 'td' && ps.td && ps.td.includes(':BIST'))) {
      bistTickers.push(tk);
    }
  }
  // Also mark existing PS entries with :BIST as bist source
  for (const tk of bistTickers) {
    if (PS[tk].s === 'td') {
      PS[tk].s = 'bist';
    } // redirect from TD to proxy
  }

  // 4b. TEFAS funds — one request covers every code. Same helper as the server-API branch.
  {
    const t = await _applyTefas(tks);
    ok += t.ok;
    fail += t.missing.length;
  }

  // Collect YF-source tickers with custom Yahoo symbols (e.g. SAP → SAP.DE)
  const proxySuffixMap = {};
  bistTickers.forEach(tk => { proxySuffixMap[tk] = tk + '.IS'; });
  for (const tk of tks) {
    const ps = PS[tk];
    if (ps && ps.s === 'yf' && ps.yf) {
      bistTickers.push(tk);
      proxySuffixMap[tk] = ps.yf;
    }
  }

  if (bistProxyUrl && bistTickers.length) {
    updatePriceStatus(`YF proxy: ${bistTickers.length} symbols...`, 'dot-live');
    const bistRes = await bistProxyFetch(bistTickers, bistProxyUrl, proxySuffixMap);
    for (const tk of Object.keys(bistRes)) {
      _S.prices[tk] = bistRes[tk];
      ok++;
    }
    _noteBistHealth(bistTickers, bistRes); // deduped outage/recovery toast
    bistTickers.forEach(tk => {
      if (!bistRes[tk]) fail++;
    });
    save();
  } else if (!bistProxyUrl && bistTickers.length) {
    console.warn(
      '[BIST] No proxy URL configured —',
      bistTickers.length,
      'BIST stocks need manual entry. Set up in Settings.'
    );
  }

  // 4b. Twelve Data for US stocks/ETFs/commodities — rate-limited batches
  if (apiKey) {
    const tdItems = [];
    for (const tk of tks) {
      const ps = PS[tk];
      if (ps && ps.s === 'td' && !ps.cg) tdItems.push({ tk, td: ps.td });
    }
    // Split into chunks of TD_CREDITS_PER_MIN
    const chunks = [];
    for (let i = 0; i < tdItems.length; i += TD_CREDITS_PER_MIN)
      chunks.push(tdItems.slice(i, i + TD_CREDITS_PER_MIN));

    for (let ci = 0; ci < chunks.length; ci++) {
      if (ci > 0) {
        // Wait for rate limit reset — show countdown
        const waitStart = Date.now();
        const waitMs = TD_WAIT_MS;
        const update = () => {
          const left = Math.ceil((waitMs - (Date.now() - waitStart)) / 1000);
          updatePriceStatus(
            `${ok} fetched · Batch ${ci + 1}/${chunks.length} in ${left}s...`,
            'dot-stale'
          );
        };
        update();
        const intv = setInterval(update, 1000);
        await sleep(waitMs);
        clearInterval(intv);
      }
      updatePriceStatus(
        `Batch ${ci + 1}/${chunks.length} (${chunks[ci].length} symbols)...`,
        'dot-live'
      );
      const res = await tdFetchChunk(chunks[ci], apiKey);
      for (const tk of Object.keys(res)) {
        const ps = PS[tk];
        const p = res[tk];
        if (ps && ps.tx === 'gram') {
          // CE-08 Fix: Store raw USD/oz AND converted TRY/gram — avoid mutating fetched price
          p._rawPriceUSD = p.price; // preserve original USD/oz
          if (_S.fxRate) {
            p.price = (p._rawPriceUSD * _S.fxRate) / 31.1035;
            p.cur = 'TRY';
          }
        }
        // Note: BIST stocks now go through proxy, not TD
        const q = normalizeQuote(p);
        if (q) { _S.prices[tk] = q; ok++; }
      }
      // Count chunk failures
      chunks[ci].forEach(item => {
        if (!res[item.tk]) fail++;
      });
      save();
    }
  }

  const man = _S.holdings.filter(
    h => PS[h.ticker]?.s === 'm' && !_S.prices[h.ticker]
  ).length;
  const noBistProxy = !bistProxyUrl && bistTickers.length > 0;
  const noKey = !apiKey;
  const st = document.getElementById('priceStatus');
  const parts = [];
  if (ok > 0) parts.push(`${ok} live`);
  if (fail > 0) parts.push(`${fail} failed`);
  if (man > 0) parts.push(`${man} manual`);
  if (noBistProxy) parts.push(`${bistTickers.length} BIST need proxy`);
  const dotClass = fail === 0 && man === 0 && !noBistProxy ? 'dot-live' : 'dot-stale';
  if (noKey && !bistProxyUrl) {
    st.innerHTML = `<span class="dot dot-stale"></span>&nbsp;No API keys — <a href="#" data-action="navTo" data-args='["settings"]' style="color:var(--yel)">configure in Settings</a>`;
  } else {
    st.innerHTML = `<span class="dot ${dotClass}"></span>&nbsp;${parts.join(' · ')}`;
  }
  if (fail > 0) {
    toast(`${fail} price(s) failed to fetch`, 'a-r');
  }
  if (noBistProxy && bistTickers.length)
    toast(`${bistTickers.length} BIST stocks need proxy — configure in Settings`, 'a-r');
  if (fail > 0 || noBistProxy)
    console.warn(
      `Price fetch: ${ok} ok, ${fail} failed, ${man} manual${
        noBistProxy ? ', ' + bistTickers.length + ' BIST need proxy' : ''
      }`
    );
  try { scanTradeCrossings(_prevPrices); } catch (e) { console.warn('Alert scan err', e); }
  save();
  if (_onPricesRefreshed) _onPricesRefreshed();
  } finally {
    _priceRefreshing = false;
    document.body.classList.remove('shimmer-loading');
  }
}

// ===== POLLING SETUP =====

let eqP = null,
  crP = null;

/**
 * Setup polling intervals for price refresh
 * - eqP: equity/general refresh (default 120s)
 * - crP: crypto-specific refresh (default 60s, uses CoinGecko)
 */
export function setupPoll() {
  if (eqP) clearInterval(eqP);
  if (crP) clearInterval(crP);
  eqP = setInterval(
    () => refreshPrices().catch(e => console.error('[Price] Poll error:', e)),
    (_S.settings.eqInt || 120) * 1000
  );
  crP = setInterval(async () => {
    if (_priceRefreshing) return; // DI-02: skip crypto poll while main refresh is running
    // apiKey removed — CoinGecko is free, no key needed for crypto poll
    const tks = [
      ...new Set([
        ..._S.holdings
          .filter(h => h.type === 'Crypto')
          .map(h => h.ticker),
        ..._S.trades
          .filter(t => t.type === 'Crypto' && t.status !== 'Closed')
          .map(t => t.ticker)
      ])
    ];
    // Always use CoinGecko for crypto polling (free, no rate limit)
    const cgIds = tks.map(tk => PS[tk]?.cg).filter(Boolean);
    if (cgIds.length) {
      const d = await cgFetch(cgIds.join(','));
      if (d)
        tks.forEach(tk => {
          const cg = PS[tk]?.cg;
          if (cg && d[cg])
            _S.prices[tk] = {
              price: d[cg].usd,
              changePct: d[cg].usd_24h_change || 0,
              src: 'auto',
              ts: Date.now(),
              cur: 'USD'
            };
        });
    }
    save(); // STABILIZATION: persist crypto price updates
    if (_onPricesRefreshed) _onPricesRefreshed();
  }, (_S.settings.crInt || 60) * 1000);
}
